/**
 * Los datos de la cuenta: a dónde ir para cambiarlos.
 *
 * Era un desplegable con el componente incrustado de Stripe
 * (`ConnectAccountManagement`), y se abría en blanco. Las cuentas que crea
 * véltica tienen el panel de Stripe COMPLETO y es Stripe quien les pide los
 * datos (ver `createConnectedAccount` en el worker: `stripe_dashboard: 'full'`),
 * y en ese tipo de cuenta el componente no pinta nada ni avisa de error: los
 * datos del negocio y el banco se editan en el panel propio del vendedor.
 *
 * Así que aquí van dos enlaces directos a las dos páginas de ese panel que más
 * se tocan —el negocio y el banco—. Un enlace y no un botón que pida algo al
 * servidor: son URLs fijas, y abrirlas tras un `await` haría que el navegador
 * las bloqueara como popup.
 */
import type { ReactNode } from 'react'
import { useI18n } from '../../../lib/i18n'
import { IconBuildingBank, IconBuildingStore, IconExternalLink } from '@tabler/icons-react'

const STRIPE_BUSINESS_URL = 'https://dashboard.stripe.com/settings/business-details'
const STRIPE_BANK_URL = 'https://dashboard.stripe.com/settings/payouts'

/**
 * Una sola lista: las dos filas que llevan al panel de Stripe y, detrás, lo que
 * quien la usa quiera añadir (`children`: desconectar). Eran dos tarjetas con su
 * cabecera cada una para tres cosas de una línea.
 */
export function StripeAccountManagement({ children, icon, title, subtitle }: {
  children?: ReactNode
  /** La cabecera, cuando el sitio donde se enseña pide otra. */
  icon?: ReactNode
  title?: string
  subtitle?: string
}) {
  const { t } = useI18n()

  /* Cabecera y un recuadro por fila, como las tarjetas de General: se ve
     de un vistazo qué es cada cosa y dónde empieza y acaba. */
  return (
    <section className="dash-settings-card dash-general-card dash-stripe-account-list">
      <div className="dash-settings-card-head">
        <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
          {icon ?? <IconBuildingBank size={18} />}
        </span>
        <div className="dash-settings-card-head-copy">
          <h3>{title ?? t('stripe.account.dataTitle')}</h3>
          <p>{subtitle ?? t('stripe.account.dataSubtitle')}</p>
        </div>
      </div>
      <a className="dash-stripe-account-row" href={STRIPE_BUSINESS_URL} target="_blank" rel="noreferrer">
        <IconBuildingStore size={18} aria-hidden="true" />
        <span>
          <strong>{t('stripe.account.business')}</strong>
          <small>{t('stripe.account.businessBody')}</small>
        </span>
        <IconExternalLink size={14} aria-hidden="true" />
      </a>
      <a className="dash-stripe-account-row" href={STRIPE_BANK_URL} target="_blank" rel="noreferrer">
        <IconBuildingBank size={18} aria-hidden="true" />
        <span>
          <strong>{t('stripe.account.bank')}</strong>
          <small>{t('stripe.account.bankBody')}</small>
        </span>
        <IconExternalLink size={14} aria-hidden="true" />
      </a>
      {children}
    </section>
  )
}
